import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import creatorsApi from "../api/creatorsApi.tsx";
import {
  CreatorInteractionsData,
  DownloadCreatorsParams,
} from "../types/creatorsTypes.tsx";
import { handleThunkError } from "./handleThunkError";

interface ApiError {
  detail: string;
}

interface FetchCreatorsParams {
  persona_id?: number;
  interest_id?: string;
}

export const fetchCreatorsData = createAsyncThunk(
  "creators/fetchData",
  async (params: FetchCreatorsParams | void, thunkAPI) => {
    try {
      const response = await creatorsApi.fetchCreatorsList(params || undefined);
      return response.data;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unexpected error occurred while fetching creators.",
      );
      return thunkAPI.rejectWithValue("Failed to fetch creators.");
    }
  },
);

export const fetchManageCreatorsList = createAsyncThunk(
  "creators/fetchManageCreatorsList",
  async (_, thunkAPI) => {
    try {
      const response = await creatorsApi.fetchManageCreatorsList();
      return response.data;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unexpected error occurred while fetching creators.",
      );
      return thunkAPI.rejectWithValue("Failed to fetch creators.");
    }
  },
);

export const fetchCreatorById = createAsyncThunk(
  "creators/fetchCreatorById",
  async (id: number, thunkAPI) => {
    try {
      const response = await creatorsApi.fetchCreatorById(id);
      return response.data;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unexpected error occurred while fetching creator information.",
      );
      return thunkAPI.rejectWithValue(
        "Failed to fetch creator contact information.",
      );
    }
  },
);

export const fetchCreatorContactsById = createAsyncThunk(
  "creators/fetchContacts",
  async (id: number, thunkAPI) => {
    try {
      const response = await creatorsApi.fetchCreatorContactsById(id);
      return response.data;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unexpected error occurred while fetching creator contact information.",
      );
      return thunkAPI.rejectWithValue(
        "Failed to fetch creator contact information.",
      );
    }
  },
);

export const interactionsCreator = createAsyncThunk(
  "creators/interactionsCreator",
  async (data: CreatorInteractionsData, thunkAPI) => {
    try {
      await creatorsApi.interactionsCreator(data);
      return data;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unexpected error occurred while switching creator status.",
      );
      return thunkAPI.rejectWithValue("Failed to switch status.");
    }
  },
);

export const deleteStatusCreator = createAsyncThunk(
  "creators/deleteStatusCreator",
  async (id: number, thunkAPI) => {
    try {
      await creatorsApi.deleteStatusCreator(id);
      return id;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unexpected error occurred while switching creator status.",
      );
      return thunkAPI.rejectWithValue("Failed to switch status.");
    }
  },
);

export const fetchReasonsForHide = createAsyncThunk(
  "creators/fetchReasonsForHide",
  async (_, thunkAPI) => {
    try {
      const response = await creatorsApi.fetchReasonsForHide();
      return response.data;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unexpected error occurred while getting creator hide reasons.",
      );
      return thunkAPI.rejectWithValue("Failed to get reasons.");
    }
  },
);

export const downloadCreatorsList = createAsyncThunk(
  "creators/downloadCreatorsList",
  async (body: DownloadCreatorsParams, thunkAPI) => {
    try {
      const response = await creatorsApi.downloadCreatorsList(body);
      if (response.status === 200) {
        const blob = response.data;
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download =
          response.headers["content-disposition"].match(
            /filename="(.+?)"/,
          )?.[1];
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }
    } catch (e: unknown) {
      const error = e as AxiosError<ApiError>;
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || "Failed to download creator list.",
      );
    }
  },
);
